import {
  BrandMark,
  CELL,
  GRID,
  H,
  TileMosaic,
  W,
  buildField,
  cropFor,
  hashSeed,
  mulberry32,
  orient,
  type Composition,
  type Crop,
  type Tile,
} from '@/components/pattern-field'
import { cn } from '@/lib/utils'

/**
 * Card artwork for blog posts.
 *
 * The product cards split their thumbnail in two — brand pattern on the left,
 * product artwork on the right. A post has no product to stand opposite, so it
 * takes the brand side alone and runs it the full width of the band, with the
 * mark centred on the field rather than sitting in the left third.
 *
 * Everything is drawn from the post's slug: which composition it gets, which
 * way that composition faces, which gradient the tiles crop from, and where
 * each crop lands. A post therefore keeps one artwork for good — across
 * renders, across pages, and between the server and client passes, which is
 * what keeps hydration quiet without the fixed-seed-then-re-roll dance
 * ProductThumb needs for its per-load shuffle.
 *
 * 8 compositions × 4 orientations × 6 gradients is 192 combinations, but they
 * are drawn independently per post rather than dealt out, so spacing them is a
 * matter of odds, not something this guarantees — 19 independent draws from 32
 * field variants only ever reach 14 of them, which is what the maths predicts,
 * not a fault in the stream.
 *
 * What that works out to for the 19 posts in hand: no view ever renders two
 * identical thumbnails, but two cards can share a layout in different
 * gradients — page 3 by date, and the "Digital Transformation" and
 * "Personalization" tag views. The single pair that collides on both axes
 * (ai-powered-fraud-prevention and data-driven-insights) shares no tag and no
 * date page, so it never co-locates.
 *
 * If the corpus grows enough for that to grate, stratify — deal each post a
 * distinct field from a stable global ordering — rather than widening the
 * pool, which barely moves the odds.
 */

/** The same six artworks the product thumbs and the solutions deck crop from. */
const ART = [
  '/gradients/01.webp',
  '/gradients/02.webp',
  '/gradients/03.webp',
  '/gradients/04.webp',
  '/gradients/05.webp',
  '/gradients/06.webp',
] as const

/**
 * Eight rhythms, each a different way of distributing weight. All of them
 * leave the mark's cells open by construction: `clearOf` below is the safety
 * net, not the mechanism. A `+` needs a cell that clears the footprint with
 * room for its rotated bounding box — cols 0–2 or 8–10 at any row, or any
 * column in rows 0 and 6.
 */
const COMPOSITIONS: readonly Composition[] = [
  // Scatter, weight to the upper left.
  {
    map: [
      '11...1.....',
      '.1.......1.',
      '..+........',
      '1........11',
      '.1.......1.',
      '.11......1.',
      '.......1...',
    ],
  },
  // Banded rows with a quiet second line.
  {
    map: [
      '.1......11.',
      '...1.......',
      '11........1',
      '.1.......1.',
      '1.........1',
      '.........1.',
      '..1..+..1..',
    ],
  },
  // Standing columns.
  {
    map: [
      '..1....1...',
      '1........11',
      '.1........1',
      '11.........',
      '.1.......1.',
      '...........',
      '...1..+.1..',
    ],
  },
  // Weighted to a broad base, thinning upward.
  {
    map: [
      '....1......',
      '.1........1',
      '1..........',
      '.........1.',
      '.1.......11',
      '11+.....1..',
      '.1.11...11.',
    ],
  },
  // Climbing to the upper right.
  {
    map: [
      '.......1.+.',
      '....1......',
      '1.........1',
      '.1.......11',
      '11.......1.',
      '......1....',
      '11.......1.',
    ],
  },
  // Quiet, with wide runs top and bottom.
  {
    map: [
      '11.......11',
      '.........+.',
      '.1........1',
      '1........11',
      '.11......1.',
      '...........',
      '..1.....11.',
    ],
  },
  // Two bands, weight to the right.
  {
    map: [
      '1.......1..',
      '.....1....1',
      '11.........',
      '.........11',
      '.1........1',
      '......1....',
      '..+.....11.',
    ],
  },
  // Sparse drift across the diagonal.
  {
    map: [
      '..11.....1.',
      '1..........',
      '.1.........',
      '11........1',
      '.........1.',
      '...1...1...',
      '.1......+..',
    ],
  },
]

/** The cells the mark covers: cols 4–6, rows 2–4. Wider than the mark itself,
    which keeps the field off its shoulders rather than merely off its
    outline — and symmetric about both axes, so `orient` can't walk a tile
    into it. */
const MARK_ZONE = {
  x0: 4 * CELL,
  x1: 7 * CELL,
  y0: 2 * CELL,
  y1: 5 * CELL,
}

const FLIPS = [0, 1, 2, 3]

const FIELDS = COMPOSITIONS.flatMap((composition, index) =>
  FLIPS.map((flip) =>
    buildField(
      { map: orient(composition.map, flip) },
      MARK_ZONE,
      `BlogThumb composition ${index} flip ${flip}`
    )
  )
)

/** The mark's height in viewBox units, centred on the field. */
export const MARK_H = 104

export interface BlogField {
  tiles: Tile[]
  crops: Crop[]
  /** Public path of the gradient the tiles and the mark crop from. */
  art: (typeof ART)[number]
}

/**
 * Everything a seed decides: which field, which gradient, and where every
 * tile's window lands. Shared with the OG image route so a post's social card
 * carries the same artwork as its card on the blog index.
 */
export function drawBlogField(seed: string): BlogField {
  // One stream for the whole thumbnail. Picking the field and the gradient off
  // consecutive draws decorrelates them; slicing one hash into two moduli did
  // not, and left a composition that no post ever drew.
  const rand = mulberry32(hashSeed(seed))
  const tiles = FIELDS[Math.floor(rand() * FIELDS.length)]!
  const art = ART[Math.floor(rand() * ART.length)]!
  const crops = tiles.map((t) => cropFor(rand, t))
  return { tiles, crops, art }
}

export function BlogThumb({
  seed,
  className,
}: {
  /** Anything stable per post — the slug is what callers pass. */
  seed: string
  className?: string
}) {
  const { tiles, crops, art } = drawBlogField(seed)

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid slice"
      // currentColor carries the hairline grid.
      className={cn(
        'h-full w-full text-zinc-900/[0.08] dark:text-white/[0.07]',
        className
      )}
      aria-hidden="true"
      focusable="false"
    >
      <path d={GRID} stroke="currentColor" strokeWidth="1" fill="none" />
      <TileMosaic tiles={tiles} crops={crops} art={art} />
      {/* The mark's silhouette is filled with the same artwork this post's
          field crops from — see BrandMark's `art` mode. */}
      <BrandMark cx={W / 2} cy={H / 2} height={MARK_H} art={art} />
    </svg>
  )
}

export default BlogThumb
